// =========================================================================
// ShopShell — buyer SPA chrome.
//
// Anon vs. signed-in (2026-05-20):
// shop.peptideclients.com is anonymous-browsable (operator Option D).
// The shell renders for BOTH states and switches the right cluster
// based on `useAuth().session`:
//
//   Signed-in:
//     [☰] [Logo Shop]   Home  Browse  Shops    Inbox  Help  Orders  Account ▾  🛒
//
//   Signed-out (anon visitor):
//     [☰] [Logo Shop]   Home  Browse  Shops                       Sign in   Sign up
//
//   * Desktop (>= md): three PRIMARY tabs (Home / Browse / Shops — the
//     marketplace discovery flow) live on the left for everyone. The
//     right cluster collapses to two pill CTAs (Sign in / Sign up) for
//     anon -- the operator chrome (Inbox/Help/Orders/Account/Cart) only
//     makes sense once the user has a session and an org bound.
//
//   * Phone (< md): hamburger drawer. For anon visitors the drawer
//     hides the auth-only links and surfaces Sign in / Sign up CTAs at
//     the footer slot (where Sign out lives for signed-in users).
//
// CartIconBadge already self-hides for anon (returns null when no
// session id), so we don't need to wrap it in a session check here --
// it just renders nothing for anon visitors. Same with
// ShopMessageNotificationWatcher (only mounted when both session and
// activeOrgId are present).
//
// The account menu (`ShopAccountMenu`) is inlined rather than promoted
// to `@pc/ui` because no other app needs the exact layout yet; if
// admin/sell ever grow the same pattern, lift it to `@pc/ui/Menu`.
// =========================================================================

import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { ShopMessageNotificationWatcher } from '@pc/shop-ui';
import { BrandLockup, Button, Drawer, Rule, ToastProvider } from '@pc/ui';
import { helpUnreadCountForCaller } from '@pc/db';
import { useRouteTracking } from '@pc/analytics-client';
import { FooterLanguagePicker, useApplyAccountLocale } from '@pc/i18n';
import { env } from '../env';
import { useAuth } from '../auth/AuthProvider';
import { useActiveOrg } from '../auth/ActiveOrgProvider';
import { CartIconBadge } from '../components/CartIconBadge';

interface NavItem {
  to: string;
  label: string;
  end?: boolean;
}

// Three high-intent discovery tabs — always visible on desktop, top of
// the drawer on phone. Order matters: Home is the marketplace landing,
// Browse is the full catalog, Shops is the vendor directory. Anything
// else lives in the right-side utility cluster or the account menu.
const PRIMARY_NAV: NavItem[] = [
  { to: '/', label: 'Home', end: true },
  { to: '/browse', label: 'Browse' },
  { to: '/shops', label: 'Shops' },
];

// Mid-frequency utility links that still warrant a top-bar slot on
// desktop. Inbox + Help carry unread badges; Orders is the buyer's
// post-purchase landing surface.
const UTILITY_NAV: NavItem[] = [
  { to: '/inbox', label: 'Inbox' },
  { to: '/help', label: 'Help' },
  { to: '/orders', label: 'Orders' },
];

// Low-frequency account surfaces — collapsed into the Account ▾
// dropdown on desktop, listed in the drawer on phone.
const ACCOUNT_NAV: NavItem[] = [
  { to: '/following', label: 'Following' },
  { to: '/purchase-orders', label: 'Purchase orders' },
  { to: '/addresses', label: 'Addresses' },
];

// Full ordered list for the mobile drawer. Mirrors the desktop visual
// hierarchy top-to-bottom.
const ALL_NAV: NavItem[] = [...PRIMARY_NAV, ...UTILITY_NAV, ...ACCOUNT_NAV];

export function ShopShell() {
  const { session, signOut, loading: authLoading } = useAuth();
  // Account-level language preference follows the user to any device.
  useApplyAccountLocale(Boolean(session));
  const { activeOrgId } = useActiveOrg();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { t } = useTranslation();

  // Anon visitors get the browse-only chrome: PRIMARY_NAV stays
  // visible (Home / Browse / Shops) but the operator cluster
  // (UTILITY_NAV + ACCOUNT_NAV + sign-out footer) is swapped for
  // Sign in / Sign up CTAs. We compute this once per render rather
  // than scattering ternaries through the JSX so the two
  // presentations stay obviously divergent in `git blame`.
  const isAnon = !session;

  // Where Sign in / Sign up bounce to. `?next=<current>` lands the
  // user back on the same browse page after auth so a "Sign in to
  // follow this vendor" click on /@yimei-peptides round-trips them
  // back to /@yimei-peptides instead of dumping them on the home
  // page. Skipped for the root path because `next=/` is the default.
  const currentPath = location.pathname + location.search + location.hash;
  const signInHref =
    currentPath && currentPath !== '/'
      ? `/sign-in?next=${encodeURIComponent(currentPath)}`
      : '/sign-in';
  // Sign-up lives in the operator app (apps/web) -- the shop SPA
  // doesn't host a sign-up form. We pipe the user across with the
  // `?next` query param recognized by apps/web's auth router so they
  // round-trip back to shop after creating an account. `env.webAppUrl`
  // is the canonical operator-app URL (https://app.peptideclients.com
  // in production); the shop origin is reconstructed client-side so
  // any custom port / preview deploy still funnels back correctly.
  const shopOrigin =
    typeof window !== 'undefined' ? window.location.origin : '';
  const signUpReturnUrl =
    shopOrigin && currentPath ? `${shopOrigin}${currentPath}` : shopOrigin;
  const signUpHref = `${env.webAppUrl}/sign-in?mode=signup${
    signUpReturnUrl ? `&next=${encodeURIComponent(signUpReturnUrl)}` : ''
  }`;

  const helpUnread = useQuery({
    queryKey: ['help', 'unread'],
    queryFn: () => helpUnreadCountForCaller(),
    enabled: !!session,
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
  const helpUnreadCount = helpUnread.data ?? 0;

  // Self-hosted pageview analytics. Pause until the auth provider has
  // settled (loading=true) so we don't double-fire on the first paint
  // when the session hydrates. After loading resolves we track for
  // BOTH anon and signed-in visitors -- anon browse pageviews are
  // legitimate marketplace traffic and the analytics-client tolerates
  // anon callers (sends with anon key, no user_id stamped). The
  // public-storefront branch (/@:slug) never reaches this shell -- it
  // lives in PublicStorefrontApp and tracks its own paint.
  useRouteTracking({ paused: authLoading });

  // Close the mobile drawer on route change so navigating from inside
  // the drawer doesn't leave it stuck open.
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  return (
    <ToastProvider>
      {session && activeOrgId ? (
        <ShopMessageNotificationWatcher
          side="buyer"
          buyerOrgId={activeOrgId}
          inboxBasePath="/inbox"
        />
      ) : null}
      <div className="min-h-screen flex flex-col bg-paper">
        <header className="sticky top-0 z-30 bg-paper/85 backdrop-blur border-b border-rule">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 h-14 flex items-center gap-3 sm:gap-5 pt-[env(safe-area-inset-top)]">
            {/* Mobile hamburger — only below md. */}
            <button
              type="button"
              onClick={() => setDrawerOpen(true)}
              aria-label="Open navigation"
              aria-expanded={drawerOpen}
              className="md:hidden -ml-2 p-2 text-ink"
            >
              <HamburgerIcon />
            </button>

            <BrandLockup app="shop" className="shrink-0" />

            {/* Primary nav (left). 3 tabs only — the discovery flow.
                Visible for anon + signed-in. Everything else moves to
                the right or under Account ▾ so the top bar stays
                uncluttered. */}
            <nav className="hidden md:flex items-center gap-5 min-w-0">
              {PRIMARY_NAV.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.end}
                  className={({ isActive }) =>
                    `text-sm whitespace-nowrap ${isActive ? 'text-ink font-medium' : 'text-muted hover:text-ink'}`
                  }
                >
                  {n.label}
                </NavLink>
              ))}
            </nav>

            <div className="ml-auto flex items-center gap-4 sm:gap-5 shrink-0">
              {isAnon ? (
                // -----------------------------------------------------
                // Anon visitor: just Sign in / Sign up. The hamburger
                // drawer (below) carries the same CTAs for phone.
                // -----------------------------------------------------
                <div className="hidden sm:flex items-center gap-3">
                  <Link
                    to={signInHref}
                    className="text-sm whitespace-nowrap text-muted hover:text-ink"
                  >
                    Sign in
                  </Link>
                  <a href={signUpHref}>
                    <Button size="sm" variant="primary">
                      Sign up
                    </Button>
                  </a>
                </div>
              ) : (
                <>
                  {/* Utility nav (right). Hidden on phone — phone users
                      reach Inbox/Help/Orders via the drawer. */}
                  <nav className="hidden md:flex items-center gap-4 lg:gap-5">
                    {UTILITY_NAV.map((n) => (
                      <NavLink
                        key={n.to}
                        to={n.to}
                        className={({ isActive }) =>
                          `text-sm whitespace-nowrap inline-flex items-center gap-1.5 ${isActive ? 'text-ink font-medium' : 'text-muted hover:text-ink'}`
                        }
                      >
                        <span>{n.label}</span>
                        {n.to === '/help' && helpUnreadCount > 0 ? (
                          <UnreadBadge count={helpUnreadCount} />
                        ) : null}
                      </NavLink>
                    ))}
                  </nav>

                  <ShopAccountMenu
                    email={session?.user.email ?? null}
                    onSignOut={signOut}
                  />
                  <CartIconBadge />
                </>
              )}
            </div>
          </div>
        </header>

        {/* Mobile nav drawer (< md). Signed-in users see all nav items
            + email + sign-out. Anon visitors see just PRIMARY_NAV and
            Sign in / Sign up CTAs at the footer slot so they can reach
            every affordance without leaving the drawer. */}
        <Drawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          side="left"
          ariaLabel="Shop navigation"
        >
          <div className="shrink-0 px-5 pt-6 pb-4">
            <BrandLockup app="shop" />
          </div>
          <Rule />
          <nav className="flex-1 min-h-0 overflow-y-auto px-3 py-3 flex flex-col gap-0.5">
            {(isAnon ? PRIMARY_NAV : ALL_NAV).map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                onClick={() => setDrawerOpen(false)}
                className={({ isActive }) =>
                  `px-3 py-2.5 text-sm rounded-sm flex items-center justify-between gap-2 ${
                    isActive
                      ? 'text-ink font-medium bg-paper-2'
                      : 'text-muted hover:text-ink hover:bg-paper-2'
                  }`
                }
              >
                <span>{n.label}</span>
                {n.to === '/help' && helpUnreadCount > 0 ? (
                  <UnreadBadge count={helpUnreadCount} />
                ) : null}
              </NavLink>
            ))}
          </nav>
          <div className="shrink-0 border-t border-rule px-5 py-4 flex flex-col gap-3">
            {isAnon ? (
              <>
                <p className="text-xs text-muted">
                  Browsing as a guest. Sign in to message vendors, save
                  a cart, follow shops, and track orders.
                </p>
                <div className="flex gap-2">
                  <Link to={signInHref} onClick={() => setDrawerOpen(false)}>
                    <Button size="sm" variant="ghost">
                      Sign in
                    </Button>
                  </Link>
                  <a href={signUpHref}>
                    <Button size="sm" variant="primary">
                      Sign up
                    </Button>
                  </a>
                </div>
              </>
            ) : (
              <>
                <p className="text-xs text-muted truncate">
                  {session?.user.email}
                </p>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={signOut}
                  className="self-start"
                >
                  Sign out
                </Button>
              </>
            )}
          </div>
        </Drawer>

        <main className="flex-1 max-w-6xl mx-auto w-full px-4 sm:px-6 py-6 sm:py-8">
          <Outlet />
        </main>
        {/*
          Footer legal links point at the CANONICAL pages hosted on the
          docs site (docs.peptideclients.com). The destination is
          cross-origin to this SPA so regular <a href> is correct -- do
          NOT swap these for react-router <Link>. Per the platform
          architectural decision, legal pages live ONCE on the docs
          site so they never drift between apps.
        */}
        <footer className="border-t border-rule py-6 text-xs text-muted">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between pb-[env(safe-area-inset-bottom)]">
            <p>{t('app.footer.copyright')}</p>
            <p>
              <a href="https://docs.peptideclients.com/terms" className="hover:underline">
                {t('app.footer.terms')}
              </a>{' '}
              ·{' '}
              <a href="https://docs.peptideclients.com/privacy" className="hover:underline">
                {t('app.footer.privacy')}
              </a>{' '}
              ·{' '}
              <a
                href="https://docs.peptideclients.com/research-use-only"
                className="hover:underline"
              >
                {t('app.footer.researchUseOnly')}
              </a>
            </p>
            <FooterLanguagePicker />
          </div>
        </footer>
      </div>
    </ToastProvider>
  );
}

// -------------------------------------------------------------------------
// ShopAccountMenu
//
// Inline desktop-only account dropdown. Hidden under `md:inline-block`
// (phone users get the same items in the drawer). Closes on outside
// click, on Escape, and on any internal navigation (NavLink onClick).
// No shared popover primitive exists in @pc/ui yet — the button and
// panel are positioned with plain absolute / right-aligned classes.
// -------------------------------------------------------------------------
function ShopAccountMenu({
  email,
  onSignOut,
}: {
  email: string | null;
  onSignOut: () => void | Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('mousedown', onDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={wrapRef} className="hidden md:block relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        className="inline-flex items-center gap-1.5 text-sm text-muted hover:text-ink whitespace-nowrap"
      >
        <span>Account</span>
        <Chevron open={open} />
      </button>

      {open ? (
        <div
          role="menu"
          aria-label="Account"
          className="absolute right-0 top-full mt-2 w-60 border border-rule bg-paper shadow-lg z-40"
        >
          {email ? (
            <div className="px-4 py-3 border-b border-rule">
              <p className="text-[11px] uppercase tracking-wide text-muted">
                Signed in as
              </p>
              <p className="text-sm text-ink truncate">{email}</p>
            </div>
          ) : null}
          <nav className="py-1.5">
            {ACCOUNT_NAV.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                role="menuitem"
                onClick={() => setOpen(false)}
                className="block px-4 py-2 text-sm text-ink hover:bg-paper-2"
              >
                {n.label}
              </Link>
            ))}
          </nav>
          <div className="border-t border-rule py-1.5">
            <button
              type="button"
              role="menuitem"
              onClick={async () => {
                setOpen(false);
                await onSignOut();
              }}
              className="block w-full text-left px-4 py-2 text-sm text-clay hover:bg-paper-2"
            >
              Sign out
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function Chevron({ open }: { open: boolean }) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 12 12"
      width={10}
      height={10}
      className={`transition-transform ${open ? 'rotate-180' : ''}`}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M2.5 4.5 6 8l3.5-3.5" />
    </svg>
  );
}

function UnreadBadge({ count }: { count: number }) {
  return (
    <span
      aria-label={`${count} unread`}
      className="inline-grid place-items-center min-w-[1.1rem] h-4 px-1 text-[10px] font-medium bg-clay text-paper rounded-full tabular-nums"
    >
      {count > 9 ? '9+' : count}
    </span>
  );
}

function HamburgerIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      aria-hidden="true"
      className="w-6 h-6"
    >
      <path d="M4 7h16M4 12h16M4 17h16" />
    </svg>
  );
}
